import { createPaymentAuthorisation, getOpenBankingRefund } from "@api";
import { Button, Modal } from "@components";
import { useEffect, useState } from "react";
import InstitutionSelectStep from "./components/InstitutionSelectStep";
import AuthoriseStep from "./components/AuthoriseStep";
import AwaitPayment from "./components/AwaitPayment";
import RefundStatusScreen from "./components/RefundStatusScreen";

const STATUS_STEPS = [
  "PaymentProcessing",
  "PaymentSucceeded",
  "PaymentFailed",
  "generalError",
];

// Authorises an already-created (Initialized) open-banking refund: pick a bank,
// authorise via createPaymentAuthorisation, then show the QR/await screen and
// poll the refund status — the same click-through-to-bank flow as before.
const AuthoriseRefund = ({ refund, isOpen, onClose, onComplete }) => {
  const [submitting, setSubmitting] = useState(false);
  const [institution, setInstitution] = useState(null);
  const [institutionError, setInstitutionError] = useState(false);
  const [paymentAuth, setPaymentAuth] = useState(null);
  const [step, setStep] = useState("InstitutionSelect");

  // Reset to the first step each time the modal opens.
  useEffect(() => {
    if (isOpen) {
      setStep("InstitutionSelect");
      setInstitution(null);
      setInstitutionError(false);
      setPaymentAuth(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const close = () => {
    setStep("InstitutionSelect");
    setInstitution(null);
    setInstitutionError(false);
    setPaymentAuth(null);
    setSubmitting(false);
    onClose();
  };

  const onSuccess = () => {
    if (onComplete) {
      onComplete();
    }
    close();
  };

  const onInstitutionSelected = (selected) => {
    setInstitutionError(false);
    setInstitution(selected);
    setStep("AuthoriseOpenBanking");
  };

  const getStatus = async (id) => {
    const paymentData = await getOpenBankingRefund(id);

    switch (paymentData.status) {
      case "Processing":
        setStep("PaymentProcessing");
        setTimeout(getStatus, 1000, id);
        break;
      case "Initialized":
        setTimeout(getStatus, 1000, id);
        break;
      case "PaymentFailed":
        setStep("PaymentFailed");
        break;
      case "PaymentSucceeded":
        setStep("PaymentSucceeded");
        break;
      default:
        setTimeout(getStatus, 1000, id);
        break;
    }
  };

  const handleConfirmedOpenBanking = async () => {
    setSubmitting(true);
    const callback = window.location.origin + `/authoriserefundcallback?&id=`;
    const response = await createPaymentAuthorisation(
      refund.id,
      institution.id,
      callback,
    );

    if (response.status !== 201) {
      setSubmitting(false);
      setInstitutionError(true);
      setStep("InstitutionSelect");
      return;
    }

    setPaymentAuth(response.data);
    setStep("AwaitPayment");
    setTimeout(getStatus, 5000, refund.id);
    setSubmitting(false);
  };

  if (!isOpen || !refund) {
    return null;
  }

  const showBack = step === "AuthoriseOpenBanking";

  return (
    <Modal isOpen={isOpen} onClose={close} title="Authorise refund" size="sm">
      <div className="px-5 pb-5">
        {step === "InstitutionSelect" && (
          <InstitutionSelectStep
            institutionError={institutionError}
            onInstitutionSelected={onInstitutionSelected}
          />
        )}
        {step === "AuthoriseOpenBanking" && (
          <AuthoriseStep
            savedForm={refund}
            institution={institution}
            submitting={submitting}
            onConfirm={handleConfirmedOpenBanking}
          />
        )}
        {step === "AwaitPayment" && (
          <AwaitPayment paymentAuth={paymentAuth} institution={institution} />
        )}
        {STATUS_STEPS.includes(step) && (
          <RefundStatusScreen
            step={step}
            savedForm={refund}
            onSuccess={onSuccess}
            onTryAgain={() => setStep("InstitutionSelect")}
          />
        )}
      </div>
      {showBack && (
        <div className="flex px-5 py-4 border-t border-zinc-200">
          <Button
            color="secondary"
            disabled={submitting}
            onClick={
              step === "InstitutionSelect"
                ? close
                : () => setStep("InstitutionSelect")
            }
          >
            Back to bank selection
          </Button>
        </div>
      )}
    </Modal>
  );
};

export default AuthoriseRefund;
